import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
} from "framer-motion";
import { ArrowRight, ArrowDown, Phone, MapPin, Wind, UserCircle } from "lucide-react";
import { CABINET, DOMAINS, WHY_REFER, PATHOLOGIES, PATHOLOGY_CATEGORIES } from "@/lib/content";

function BreathingHero() {
    const [phase, setPhase] = useState("inhale");
    useEffect(() => {
        const id = setInterval(() => {
            setPhase((p) => (p === "inhale" ? "exhale" : "inhale"));
        }, 4000);
        return () => clearInterval(id);
    }, []);

    return (
        <section
            data-testid="home-hero"
            style={{
                position: "relative",
                minHeight: "100vh",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                background:
                    "radial-gradient(ellipse at 50% 60%, #fbeee9 0%, #ffffff 45%, #f5e3dc 100%)",
            }}
        >
            <div
                aria-hidden
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "min(85vh, 900px)",
                    height: "min(85vh, 900px)",
                    pointerEvents: "none",
                }}
            >
                <div className="breathe-slow" style={{ position: "absolute", inset: "8%", borderRadius: "50%", border: "1px solid var(--sage)", opacity: 0.35 }} />
                <div className="breathe" style={{ position: "absolute", inset: "20%", borderRadius: "50%", border: "1px solid var(--terra)", opacity: 0.45 }} />
                <div className="breathe-fast" style={{ position: "absolute", inset: "32%", borderRadius: "50%", border: "1px solid var(--forest)", opacity: 0.4 }} />
                <div className="breathe" style={{ position: "absolute", inset: "42%", borderRadius: "50%", background: "radial-gradient(circle, rgba(216, 155, 146, 0.28) 0%, transparent 70%)" }} />
                <img src="/assets/emblem.png" alt="" className="drift" style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "22%", opacity: 0.75 }} />
            </div>

            <div className="mx-auto" style={{ maxWidth: 1400, padding: "160px 40px 60px", width: "100%", position: "relative", zIndex: 2 }}>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1.2, ease: [0.2, 0.8, 0.2, 1] }}
                    className="max-w-4xl"
                >
                    <div className="flex items-center gap-3 mb-8">
                        <span className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                            Cabinet & domicile · Bex, Vaud
                        </span>
                        <span style={{ width: 36, height: 1, background: "var(--terra)" }} />
                    </div>

                    <h1 className="font-display" style={{ fontSize: "clamp(3.2rem, 8vw, 8rem)", lineHeight: 0.95, color: "var(--forest-dark)", fontWeight: 400, letterSpacing: "-0.03em", margin: 0 }}>
                        Le corps
                        <br />
                        <span style={{ fontStyle: "italic", color: "var(--terra)" }}>& le souffle,</span>
                        <br />
                        pris au sérieux.
                    </h1>

                    <p className="font-display mt-10" style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.35rem)", lineHeight: 1.6, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 580, margin: "40px 0 0" }}>
                        Cardio-respiratoire, rééducation du périnée et physiothérapie générale. Séances <strong style={{ color: "var(--forest-dark)", fontWeight: 500 }}>en cabinet ou à domicile</strong> — vingt-six ans de pratique clinique, au service des patients référés par leur médecin.
                    </p>

                    <div className="mt-14 flex flex-wrap items-center gap-5">
                        <Link to="/rendez-vous" data-testid="hero-cta-appointment" className="btn-primary">
                            Demander un rendez-vous <ArrowRight size={16} />
                        </Link>
                        <a href={`tel:${CABINET.phoneIntl}`} data-testid="hero-cta-phone" className="btn-ghost">
                            <Phone size={14} /> {CABINET.phone}
                        </a>
                        <Link to="/domaines" data-testid="hero-cta-domains" className="link-underline" style={{ fontSize: 14, letterSpacing: "0.03em" }}>
                            Découvrir les domaines de prise en charge
                        </Link>
                    </div>
                </motion.div>

                <div className="absolute flex items-end justify-between" style={{ left: 40, right: 40, bottom: 40, pointerEvents: "none" }}>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.5, duration: 1 }} className="flex items-center gap-3" style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--ink-soft)" }}>
                        <Wind size={14} style={{ color: "var(--terra)" }} />
                        <span>{phase === "inhale" ? "Inspirer" : "Expirer"} · 4 secondes</span>
                    </motion.div>
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1, y: [0, 6, 0] }} transition={{ delay: 1.5, duration: 2, repeat: Infinity }} style={{ fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--ink-soft)", display: "flex", alignItems: "center", gap: 8 }}>
                        Défiler <ArrowDown size={14} />
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function DomainCard({ d, index }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
    const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.5, 1, 1, 0.6]);
    const isEven = index % 2 === 1;

    return (
        <motion.article
            ref={ref}
            data-testid={`domain-card-${d.id}`}
            style={{ opacity }}
            className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 py-24 md:py-32 items-center"
        >
            <motion.div className={`md:col-span-5 ${isEven ? "md:order-2" : ""}`} style={{ y }}>
                <div style={{
                    position: "relative", aspectRatio: "1 / 1", maxWidth: 420, margin: "0 auto",
                    background: d.color === "sage"
                        ? "linear-gradient(135deg, #f5e6df 0%, #fbeee9 100%)"
                        : d.color === "terra"
                            ? "linear-gradient(135deg, #f0cfc7 0%, #fbeee9 100%)"
                            : "linear-gradient(135deg, #ecdfd7 0%, #fbeee9 100%)",
                    borderRadius: 4, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
                }}>
                    <img src={d.picto} alt="" style={{ width: "58%", maxWidth: 260, filter: "brightness(0.4)", opacity: 0.85, position: "relative", zIndex: 2 }} />
                    <div className="breathe-slow" style={{ position: "absolute", inset: "12%", borderRadius: "50%", border: "1px solid rgba(107, 85, 72, 0.15)" }} />
                    <div className="breathe" style={{ position: "absolute", inset: "22%", borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.4)" }} />
                </div>
            </motion.div>

            <div className={`md:col-span-7 ${isEven ? "md:order-1" : ""}`}>
                <div className="flex items-center gap-4 mb-5">
                    <span className="font-display" style={{ fontSize: 60, color: "var(--terra)", lineHeight: 1, fontWeight: 500 }}>{d.num}</span>
                    <div style={{ flex: 1 }}>
                        <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>{d.subtitle}</div>
                        <div className="mt-2" style={{ height: 1, background: "var(--terra)", width: "40%" }} />
                    </div>
                </div>
                <h3 className="font-display" style={{ fontSize: "clamp(2.4rem, 4.5vw, 4rem)", lineHeight: 1, color: "var(--forest-dark)", margin: "8px 0 24px", fontWeight: 400 }}>{d.title}</h3>
                <p className="font-display" style={{ fontSize: "1.15rem", lineHeight: 1.65, color: "var(--ink-mid)", maxWidth: 520, fontStyle: "italic" }}>{d.lead}</p>

                <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2" style={{ listStyle: "none", padding: 0, maxWidth: 560 }}>
                    {d.indications.slice(0, 6).map((ind) => (
                        <li key={ind} style={{ fontSize: 13.5, color: "var(--ink-mid)", lineHeight: 1.5, paddingLeft: 14, position: "relative" }}>
                            <span style={{ position: "absolute", left: 0, top: 9, width: 6, height: 1, background: "var(--terra)" }} />
                            {ind}
                        </li>
                    ))}
                </ul>

                <Link to={`/domaines#${d.id}`} data-testid={`domain-detail-link-${d.id}`} className="link-underline inline-flex items-center gap-2 mt-8" style={{ fontSize: 14, letterSpacing: "0.03em" }}>
                    Approche détaillée <ArrowRight size={14} />
                </Link>
            </div>
        </motion.article>
    );
}

function DomainsSection() {
    return (
        <section data-testid="home-domains-section" style={{ padding: "80px 40px 40px", position: "relative" }}>
            <div className="mx-auto" style={{ maxWidth: 1400 }}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }} className="max-w-3xl">
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Trois domaines de prise en charge</div>
                    <div className="divider-terra mt-3" />
                    <h2 className="font-display mt-6" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)", lineHeight: 1.05, color: "var(--forest-dark)", fontWeight: 400 }}>
                        Une pratique <span style={{ fontStyle: "italic", color: "var(--terra)" }}>spécialisée</span>,
                        <br />un regard clinique <span style={{ fontStyle: "italic" }}>large.</span>
                    </h2>
                </motion.div>

                <div className="mt-12">
                    {DOMAINS.map((d, i) => (<DomainCard key={d.id} d={d} index={i} />))}
                </div>
            </div>
        </section>
    );
}

function WhyReferSection() {
    return (
        <section data-testid="home-why-refer" style={{ background: "var(--forest-deep)", color: "var(--cream)", padding: "120px 40px", position: "relative", overflow: "hidden" }}>
            <div aria-hidden className="breathe-slow" style={{ position: "absolute", right: -200, top: -200, width: 700, height: 700, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.15)", pointerEvents: "none" }} />
            <div aria-hidden className="breathe" style={{ position: "absolute", left: -100, bottom: -100, width: 500, height: 500, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.1)", pointerEvents: "none" }} />

            <div className="mx-auto relative" style={{ maxWidth: 1400, zIndex: 2 }}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>À l'attention des médecins référents</div>
                        <div className="divider-terra mt-3" style={{ background: "rgba(255,255,255,0.15)" }} />
                        <h2 className="font-display mt-6" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: 1.05, color: "var(--cream)", fontWeight: 400 }}>
                            Pourquoi <span style={{ fontStyle: "italic", color: "var(--terra-light)" }}>adresser</span>
                            <br />vos patients ?
                        </h2>
                        <p className="font-display mt-6" style={{ fontSize: "1.1rem", lineHeight: 1.6, color: "var(--terra-light)", fontStyle: "italic", maxWidth: 340 }}>
                            Cinq engagements simples qui structurent la prise en charge.
                        </p>
                    </div>

                    <div className="md:col-span-8">
                        <ul style={{ listStyle: "none", padding: 0 }}>
                            {WHY_REFER.map((item, i) => (
                                <motion.li key={item.heading} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay: i * 0.08, ease: [0.2, 0.8, 0.2, 1] }} className="grid grid-cols-12 gap-6 items-start py-8" style={{ borderTop: i === 0 ? "1px solid rgba(255,255,255,0.12)" : "none", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                                    <div className="col-span-2 md:col-span-1 font-display" style={{ fontSize: 20, color: "var(--terra-light)", fontStyle: "italic" }}>
                                        0{i + 1}
                                    </div>
                                    <div className="col-span-10 md:col-span-11">
                                        <div className="font-display" style={{ fontSize: "clamp(1.3rem, 2vw, 1.7rem)", color: "var(--cream)", lineHeight: 1.15, marginBottom: 8 }}>{item.heading}</div>
                                        <div style={{ fontSize: 14.5, color: "rgba(251, 248, 243, 0.7)", lineHeight: 1.6, maxWidth: 640 }}>{item.body}</div>
                                    </div>
                                </motion.li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ClosingCTA() {
    return (
        <section data-testid="home-closing-cta" style={{ padding: "120px 40px" }}>
            <div className="mx-auto text-center" style={{ maxWidth: 900 }}>
                <div className="font-mono-clinical mx-auto" style={{ color: "var(--terra)" }}>Prise de contact</div>
                <div className="mx-auto mt-3" style={{ width: 48, height: 1, background: "var(--terra)" }} />
                <h2 className="font-display mt-8" style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", lineHeight: 1, color: "var(--forest-dark)", fontWeight: 400 }}>
                    Un patient <span style={{ fontStyle: "italic", color: "var(--terra)" }}>à orienter</span> ?
                </h2>
                <p className="font-display mt-8 mx-auto" style={{ fontSize: "1.2rem", lineHeight: 1.6, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 640 }}>
                    Formulaire de prise de rendez-vous en ligne, ou contact direct par téléphone du lundi au vendredi.
                </p>
                <div className="flex flex-wrap justify-center gap-5 mt-12">
                    <Link to="/rendez-vous" data-testid="closing-cta-appointment" className="btn-primary">Prendre rendez-vous <ArrowRight size={16} /></Link>
                    <a href={`tel:${CABINET.phoneIntl}`} data-testid="closing-cta-phone" className="btn-ghost"><Phone size={14} />{CABINET.phone}</a>
                </div>
                <div className="mt-14 flex flex-wrap justify-center gap-8" style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                    <span className="flex items-center gap-2"><MapPin size={13} style={{ color: "var(--terra)" }} />{CABINET.address} · {CABINET.city}</span>
                    <span>Consultation sur rendez-vous · Français</span>
                </div>
            </div>
        </section>
    );
}

export default function Home() {
    return (
        <main data-testid="home-page">
            <BreathingHero />
            <DomainsSection />
            <PathologiesSection />
            <TeamSection />
            <BookingPreview />
            <WhyReferSection />
            <ClosingCTA />
        </main>
    );
}

function TeamSection() {
    return (
        <section data-testid="home-team" style={{ padding: "120px 40px" }}>
            <div className="mx-auto" style={{ maxWidth: 1400 }}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9 }}
                        className="md:col-span-5"
                        style={{ position: "relative" }}
                    >
                        <div
                            data-testid="home-portrait-placeholder"
                            style={{
                                aspectRatio: "3 / 4",
                                background: "linear-gradient(135deg, var(--cream-warm) 0%, var(--terra-light) 100%)",
                                border: "1px solid var(--hairline)",
                                borderRadius: 4,
                                position: "relative", overflow: "hidden",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <div className="breathe-slow" style={{ position: "absolute", inset: "12%", borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.35)" }} />
                            <div className="breathe" style={{ position: "absolute", inset: "26%", borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.25)" }} />
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, color: "var(--terra-deep)", position: "relative" }}>
                                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                    <UserCircle size={26} strokeWidth={1.2} />
                                </div>
                                <div className="font-mono-clinical" style={{ color: "var(--terra-deep)" }}>Portrait à venir</div>
                            </div>
                        </div>
                        {/* Badge */}
                        <div style={{ position: "absolute", top: 20, right: -12, background: "var(--terra)", color: "var(--cream)", padding: "8px 16px", borderRadius: 999, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 500, boxShadow: "0 6px 18px -8px rgba(216, 155, 146, 0.6)" }}>
                            Physiothérapeute diplômée
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-100px" }}
                        transition={{ duration: 0.9, delay: 0.2 }}
                        className="md:col-span-7"
                    >
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>La praticienne</div>
                        <div className="divider-terra mt-3" />
                        <h2 className="font-display mt-8" style={{ fontSize: "clamp(2.4rem, 5vw, 4.2rem)", lineHeight: 0.95, color: "var(--forest-dark)", fontWeight: 400, letterSpacing: "-0.02em" }}>
                            Ana,
                            <br />
                            <span style={{ fontStyle: "italic", color: "var(--terra)" }}>vingt-six ans</span> au chevet
                            <br />du souffle.
                        </h2>
                        <p className="font-display mt-8" style={{ fontSize: "1.15rem", lineHeight: 1.65, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 560 }}>
                            Diplômée en physiothérapie, vingt-six ans d'exercice clinique — services hospitaliers de médecine interne, chirurgie, soins intensifs et pneumologie, puis pratique ambulatoire au long cours.
                        </p>
                        <p className="mt-6" style={{ fontSize: 14.5, lineHeight: 1.7, color: "var(--ink-mid)", maxWidth: 560 }}>
                            Praticienne formatrice, elle accompagne étudiants et jeunes diplômés. Formation continue régulière en cardio-respiratoire et périnéologie.
                        </p>

                        <div className="mt-10 flex flex-wrap gap-3">
                            {["Cardio-respiratoire", "Périnée", "Physiothérapie générale", "Cabinet & domicile"].map((chip) => (
                                <span key={chip} style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", padding: "6px 14px", border: "1px solid var(--hairline)", borderRadius: 999, color: "var(--forest-dark)" }}>
                                    {chip}
                                </span>
                            ))}
                        </div>

                        <Link to="/cabinet" data-testid="team-cta-cabinet" className="btn-primary mt-10 inline-flex">
                            En savoir plus sur le parcours <ArrowRight size={16} />
                        </Link>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function BookingPreview() {
    // Generate next 5 business days
    const dates = [];
    const today = new Date();
    let d = new Date(today);
    d.setDate(d.getDate() + 1);
    while (dates.length < 5) {
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6) dates.push(new Date(d));
        d.setDate(d.getDate() + 1);
    }
    return (
        <section data-testid="home-booking" style={{ padding: "120px 40px", background: "var(--cream-warm)", borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}>
            <div className="mx-auto" style={{ maxWidth: 1400 }}>
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-100px" }} transition={{ duration: 0.8 }} className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-4">
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Prochains créneaux</div>
                        <div className="divider-terra mt-3" />
                        <h2 className="font-display mt-6" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", lineHeight: 1.05, color: "var(--forest-dark)", fontWeight: 400 }}>
                            Réservez en <span style={{ fontStyle: "italic", color: "var(--terra)" }}>un tap</span>.
                        </h2>
                        <p className="mt-6" style={{ fontSize: 14, color: "var(--ink-mid)", lineHeight: 1.6 }}>
                            Sélectionnez un créneau ci-contre : le formulaire de demande s'ouvre avec la date pré-remplie. Ana vous recontacte sous 48h.
                        </p>
                    </div>
                    <div className="md:col-span-8">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {dates.map((dt) => {
                                const iso = dt.toISOString().slice(0, 10);
                                const day = dt.toLocaleDateString("fr-CH", { weekday: "short" });
                                const num = dt.getDate();
                                const month = dt.toLocaleDateString("fr-CH", { month: "short" });
                                return (
                                    <div key={iso} data-testid={`booking-day-${iso}`} style={{ background: "var(--cream)", border: "1px solid var(--hairline)", borderRadius: 4, padding: "16px 12px", textAlign: "center" }}>
                                        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "var(--terra)", fontWeight: 500 }}>{day}</div>
                                        <div className="font-display" style={{ fontSize: 34, color: "var(--forest-dark)", lineHeight: 1, margin: "6px 0" }}>{num}</div>
                                        <div style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: "0.1em" }}>{month}</div>
                                        <div className="mt-3 flex flex-col gap-1">
                                            {["matin", "apres-midi"].map((s) => (
                                                <Link key={s} to={`/rendez-vous?date=${iso}&slot=${s}`} data-testid={`booking-slot-${iso}-${s}`} style={{ fontSize: 10.5, padding: "4px 8px", border: "1px solid var(--hairline)", borderRadius: 3, color: "var(--forest-dark)", textDecoration: "none", letterSpacing: "0.05em" }}>
                                                    {s === "matin" ? "Matin" : "Après-midi"}
                                                </Link>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        <div className="mt-8" style={{ fontSize: 12, color: "var(--ink-soft)", fontStyle: "italic" }}>
                            Aperçu indicatif · La disponibilité définitive est confirmée par Ana au moment du rappel.
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

function PathologiesSection() {
    const [cat, setCat] = useState("all");
    const visible = cat === "all" ? PATHOLOGIES : PATHOLOGIES.filter((p) => p.cat === cat);
    return (
        <section
            data-testid="home-pathologies"
            style={{ padding: "120px 40px", background: "var(--cream-warm)", borderTop: "1px solid var(--hairline)", borderBottom: "1px solid var(--hairline)" }}
        >
            <div className="mx-auto" style={{ maxWidth: 1400 }}>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.8, ease: [0.2, 0.8, 0.2, 1] }}
                    className="max-w-3xl mb-10"
                >
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>
                        Pathologies prises en charge
                    </div>
                    <div className="divider-terra mt-3" />
                    <h2
                        className="font-display mt-6"
                        style={{
                            fontSize: "clamp(2.2rem, 4.5vw, 3.6rem)",
                            lineHeight: 1.05,
                            color: "var(--forest-dark)",
                            fontWeight: 400,
                        }}
                    >
                        Une <span style={{ fontStyle: "italic", color: "var(--terra)" }}>cartographie</span> clinique
                        <br />pour <span style={{ fontStyle: "italic" }}>chaque</span> demande.
                    </h2>
                </motion.div>

                {/* Category filter */}
                <div className="flex flex-wrap items-center gap-3 mb-12" data-testid="patho-filter">
                    {PATHOLOGY_CATEGORIES.map((c) => (
                        <button
                            key={c.id}
                            onClick={() => setCat(c.id)}
                            data-testid={`patho-filter-${c.id}`}
                            style={{
                                background: cat === c.id ? "var(--forest-dark)" : "transparent",
                                color: cat === c.id ? "var(--cream)" : "var(--forest-dark)",
                                border: `1px solid ${cat === c.id ? "var(--forest-dark)" : "var(--hairline)"}`,
                                padding: "8px 18px", borderRadius: 999,
                                fontSize: 13, letterSpacing: "0.04em", cursor: "pointer",
                                transition: "all 0.3s ease",
                            }}
                        >
                            {c.label}
                            <span style={{ marginLeft: 8, opacity: 0.6, fontSize: 11 }}>
                                {c.id === "all" ? PATHOLOGIES.length : PATHOLOGIES.filter((p) => p.cat === c.id).length}
                            </span>
                        </button>
                    ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {visible.map((p, i) => (
                        <motion.article
                            key={p.title}
                            data-testid={`pathology-tile-${p.cat}-${i}`}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: (i % 4) * 0.05 }}
                            style={{
                                background: p.highlight ? "var(--forest-deep)" : "var(--cream)",
                                color: p.highlight ? "var(--cream)" : "var(--ink)",
                                border: `1px solid ${p.highlight ? "var(--forest-deep)" : "var(--hairline)"}`,
                                borderRadius: 4,
                                padding: "26px 24px",
                                transition: "transform 0.5s cubic-bezier(0.2, 0.8, 0.2, 1), box-shadow 0.5s ease, border-color 0.3s ease",
                                position: "relative",
                                overflow: "hidden",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-4px)";
                                e.currentTarget.style.boxShadow = "0 18px 40px -20px rgba(107, 85, 72, 0.2)";
                                if (!p.highlight) e.currentTarget.style.borderColor = "var(--terra)";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "none";
                                if (!p.highlight) e.currentTarget.style.borderColor = "var(--hairline)";
                            }}
                        >
                            {p.subtitle && (
                                <div className="font-mono-clinical mb-3" style={{ color: p.highlight ? "var(--terra-light)" : "var(--terra)" }}>
                                    {p.subtitle}
                                </div>
                            )}
                            <h3 className="font-display" style={{ fontSize: p.highlight ? 22 : 19, fontWeight: 500, lineHeight: 1.2, color: p.highlight ? "var(--cream)" : "var(--forest-dark)", marginBottom: 12, letterSpacing: "-0.005em" }}>
                                {p.title}
                            </h3>
                            <p style={{ fontSize: 13.5, lineHeight: 1.55, color: p.highlight ? "rgba(251, 248, 243, 0.78)" : "var(--ink-mid)", margin: 0 }}>
                                {p.body}
                            </p>
                            {p.highlight && (
                                <div aria-hidden className="breathe" style={{ position: "absolute", right: -30, bottom: -30, width: 140, height: 140, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.25)" }} />
                            )}
                        </motion.article>
                    ))}
                </div>

                <div className="mt-12 text-center">
                    <Link to="/rendez-vous" className="link-underline" style={{ fontSize: 14, letterSpacing: "0.03em", color: "var(--forest-dark)" }}>
                        Vous ne trouvez pas votre pathologie ? Contactez-nous
                    </Link>
                </div>
            </div>
        </section>
    );
}
